import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Image, Card, CardImage, Heading3 } from '@blueprint/ui';
import { FeatureList } from '@zoopla/listings/features';
import { SkeletonRow } from '../Skeleton/Skeleton';

export const cardWidth = 17.5;

export const CardsContainer = styled('div')(
    () => css`
        display: flex;
        overflow-x: auto;
        flex-direction: row;
        scroll-behavior: smooth;
    `
);

export const StyledLink = styled('a')(
    ({ theme: { spacing, focusRing } }) => css`
        margin: ${spacing['spacing-1']};
        text-decoration: none;
        display: inline;
        :focus {
            box-shadow: ${focusRing};
            outline: 0;
        }
        :first-of-type {
            margin-left: 1rem;
        }
    `
);

export const StyledCard = styled(Card)(
    ({ theme }) => css`
        display: flex;
        flex-direction: column;
        border-radius: ${theme.borderRadius['300']};
        padding: 0;
        height: 100%;
        width: ${cardWidth}rem;

        ${CardImage} {
            margin: 0;
            min-width: 100%;
            height: 100%;
            object-fit: cover;
        }
    `
);

export const MetaCard = styled('div')(
    ({ theme: { colors, spacing } }) => css`
        display: flex;
        width: 17.5rem;
        min-width: 17.5rem;
        align-items: center;
        justify-content: center;
        margin: ${spacing['spacing-1']};
        border-radius: ${spacing['4px']};
        background: ${colors['graphic-background-accent']};
    `
);

export const CardDetails = styled('div')(
    ({ theme: { spacing } }) => css`
        padding: ${spacing['spacing-2']};

        ${FeatureList} {
            padding-top: ${spacing['spacing-1']};
        }
    `
);

export const CardHeader = styled.div(
    () => css`
        position: relative;
        display: flex;
        justify-content: space-between;
    `
);

export const AgencyLogo = styled('div')(
    ({ theme: { spacing } }) => css`
        position: absolute;
        right: 0;
        top: 0;
        line-height: 1;
        max-width: ${spacing['80px']};

        ${Image} {
            max-height: ${spacing['48px']};
            width: auto;
            display: block;
        }
    `
);

export const PriceContainer = styled.div(() => css``);

export const StyledTitle = styled(Heading3)(
    ({ theme: { colors, spacing } }) => css`
        padding-top: ${spacing['spacing-1']};
        color: ${colors['text-title']};
    `
);

export const PriceTitleText = styled('p')(
    ({ theme }) => css`
        margin: 0;
        line-height: ${theme.type.lineHeight};
        font-size: ${theme.type.secondary};
        color: ${theme.colors['text-body']};
        clear: both;
    `
);

export const StyledLinkSkeleton = styled('div')(
    ({ theme: { spacing, focusRing } }) => css`
        margin: ${spacing['spacing-1']};
        display: inline;
        position: relative;
        :focus {
            box-shadow: ${focusRing};
            outline: 0;
        }
        :first-of-type {
            margin-left: 1rem;
        }

        &:after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;

            background: linear-gradient(
                90deg,
                rgba(0, 0, 0, 0) 35%,
                rgba(255, 255, 255, 0.3) 50%,
                rgba(0, 0, 0, 0) 65%
            );
            background-size: 300%;
            animation: shine 1.5s linear infinite;

            @media (prefers-reduced-motion: reduce) {
                animation: none;
            }
        }

        @keyframes shine {
            0% {
                background-position: 100%;
            }

            100% {
                background-position: 0%;
            }
        }
    `
);

export const ShortSkeletonRow = styled(SkeletonRow)(
    ({ theme: { spacing } }) => css`
        margin-bottom: ${spacing['8px']};
    `
);

export const CardDetailsSkeleton = styled('div')(
    ({ theme: { spacing } }) => css`
        padding: ${spacing['spacing-2']};
        height: 163px;
    `
);
